import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { sectors, syncState } from "@/plugins/LocalForagePlugin";
import { sectorsSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { ContentfulSectors, SectorContentfulInfo } from "@/types/api/content-models";
import type { InvestorsIntelligenceSector } from "@/types/entities";
import type { SectorEdge, SectorEdgeNodes } from "@/types/api/market-data";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshSectorsFromServer( authToken: string | null ): Promise< InvestorsIntelligenceSector[] > {
    let allSectors: InvestorsIntelligenceSector[] = [];

    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 50;

    let sectorPayload: InvestorsIntelligenceSector[] | undefined = [];

    do {
        const response =
            await goFetch<ContentfulSectors, PersistedQueryPayload>( {
                endpoint: 'content/graphql',
                payload: {
                    id: 'e82f7b992a593849eea4503aa3f594b9',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                },
                authToken: authToken
            } );

        if ( null != response?.data?.sectorCatalog?.sectors ) {
            sectorPayload = [ ...( response.data.sectorCatalog.sectors
                    .map( ( info: SectorContentfulInfo ): InvestorsIntelligenceSector => ( {
                        contentfulId: info.id,
                        name: info.name,
                        slug: info.slug.toLowerCase(),
                        symbol: info.short_name,
                        // Data that comes from TMI/II
                        id: 0,
                        iiChartId: 0,
                        numStocksInSector: 0,
                        numEtfsInSector: 0
                    } ) )
            ) ];

            const sectorMarketData =
                await goFetch<SectorEdgeNodes, PersistedQueryPayload>( {
                    endpoint: 'sector/graphql',
                    payload: {
                        id: '75a969867c5a3ada61b684d4a8a4f338',
                        variables: {
                            skip: page,
                            limit: pageSize
                        }
                    },
                    authToken: authToken
                } );

            const tmiSectors = new Map< string, InvestorsIntelligenceSector >();
            if ( null != sectorMarketData?.data?.sectors?.edges ) {
                sectorMarketData.data.sectors.edges.forEach( ( edge: SectorEdge ) => {
                    const node = edge.node;
                    tmiSectors.set( node.symbol.toLowerCase(), {
                        id: node.id,
                        iiChartId: node.indexChartId,
                        name: node.name,
                        numStocksInSector: node.stockCount,
                        numEtfsInSector: node.etfCount,
                        symbol: node.symbol,
                        // Data that comes from Contentful
                        slug: '',
                        contentfulId: '',
                    } );
                } );

                sectorPayload.forEach( ( contentfulSector: InvestorsIntelligenceSector ) => {
                    const tmiSector: InvestorsIntelligenceSector | undefined = tmiSectors.get( contentfulSector.symbol.toLowerCase() );

                    if ( tmiSector ) {
                        tmiSector.slug = contentfulSector.slug;
                        tmiSector.contentfulId = contentfulSector.contentfulId;
                    }
                } );

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ sectorPayload.length } sector items took ${ t1 - t0 } ms` );

                const mergedSectors = Array.from( tmiSectors, ( [ _, value ] ) => value );
                allSectors = [ ...mergedSectors, ...allSectors ];
            }
        }

        // update the page #
        page++;
    } while ( !!sectorPayload && sectorPayload.length >= pageSize );

    return allSectors;
}

export async function loadSectorMaps( sectorByContentfulIdMap: Map< string, InvestorsIntelligenceSector >,
                                      sectorByTmiIdMap: Map< number, InvestorsIntelligenceSector >,
                                      authToken: string | null ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        // Verify that IDB has been initialized
        if ( typeof sectors === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( sectorsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            updated = true;

            // Retrieve the latest collection of sectors from the server
            const sectorsReceived: InvestorsIntelligenceSector[] = await refreshSectorsFromServer( authToken );

            // Store the items recently retrieved
            await sectors.ready();
            for ( const sector of sectorsReceived ) {
                await sectors.setItem( sector.contentfulId, cloneDeep( sector ) );
            }

            const nextSyncThresholdInMillis = DateTime.utc().plus( { days: 30 } ).toMillis();
            await syncState.setItem( sectorsSyncKey, nextSyncThresholdInMillis );

            const t1: number = performance.now();
            logger.info( `[ sync-worker ] Refreshed ${ sectorsReceived.length } sectors from server in ${ t1 - t0 } ms` );
        }

        // Make sure maps is empty
        sectorByTmiIdMap.clear();
        sectorByContentfulIdMap.clear();
        await sectors.ready();
        // Iterate over all elements within IDB
        await sectors.iterate( ( sector: InvestorsIntelligenceSector ) => {
            sectorByContentfulIdMap.set( sector.contentfulId, sector );
            sectorByTmiIdMap.set( sector.id, sector );
            return;
        } );
    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
